export interface ParticleNode {
  homeX: number
  homeY: number
  size: number
  phase: number
  alpha: number
}

export interface ParticleEdge {
  from: number
  to: number
}

export interface ProbeStrand {
  origin: number
  angle: number
  length: number
  bend: number
  phase: number
}

export interface ParticleTopology {
  nodes: ParticleNode[]
  edges: ParticleEdge[]
  strands: ProbeStrand[]
}

const MIN_NODE_COUNT = 48
const MAX_NODE_COUNT = 180
const MIN_STRAND_COUNT = 3
const MAX_STRAND_COUNT = 14
const MAX_NEIGHBORS = 4
const ELLIPSE_Y_SCALE = 0.74
const OUTER_RADIUS = 0.65
const FULL_TURN = Math.PI * 2

function hashSeed(value: string): number {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

function randomFrom(seed: number): () => number {
  let value = seed
  return () => {
    value += 0x6D2B79F5
    let result = value
    result = Math.imul(result ^ (result >>> 15), result | 1)
    result ^= result + Math.imul(result ^ (result >>> 7), result | 61)
    return ((result ^ (result >>> 14)) >>> 0) / 4294967296
  }
}

function boundedInteger(value: number, minimum: number, maximum: number): number {
  const finiteValue = Number.isFinite(value) ? Math.round(value) : minimum
  return Math.min(maximum, Math.max(minimum, finiteValue))
}

function normalizedRadius(node: ParticleNode): number {
  return Math.hypot(node.homeX, node.homeY)
}

function createEdges(nodes: ParticleNode[]): ParticleEdge[] {
  const edges = new Map<string, ParticleEdge>()

  for (let from = 0; from < nodes.length; from += 1) {
    const sourceNode = nodes[from]
    if (!sourceNode) {
      continue
    }
    const nearest = nodes
      .map((node, to) => ({
        to,
        distanceSquared:
          (node.homeX - sourceNode.homeX) ** 2
          + (node.homeY - sourceNode.homeY) ** 2,
      }))
      .filter(({ to }) => to !== from)
      .sort((first, second) =>
        first.distanceSquared - second.distanceSquared || first.to - second.to)
      .slice(0, MAX_NEIGHBORS)

    for (const { to } of nearest) {
      const lower = Math.min(from, to)
      const upper = Math.max(from, to)
      edges.set(`${lower}:${upper}`, { from: lower, to: upper })
    }
  }

  return [...edges.values()]
    .sort((first, second) => first.from - second.from || first.to - second.to)
}

export function createParticleTopology(
  seed: string,
  requestedNodeCount: number,
  requestedStrandCount: number,
): ParticleTopology {
  const nodeCount = boundedInteger(requestedNodeCount, MIN_NODE_COUNT, MAX_NODE_COUNT)
  const strandCount = boundedInteger(
    requestedStrandCount,
    MIN_STRAND_COUNT,
    MAX_STRAND_COUNT,
  )
  const random = randomFrom(hashSeed(seed))

  const nodes = Array.from({ length: nodeCount }, (_, index): ParticleNode => {
    const angle = random() * FULL_TURN
    const ellipseScaleAtAngle = Math.hypot(
      Math.cos(angle),
      Math.sin(angle) * ELLIPSE_Y_SCALE,
    )
    const minimumStrandRadius = Math.min(0.99, 0.66 / ellipseScaleAtAngle)
    const sampledRadius = index < strandCount
      ? Math.sqrt(
        minimumStrandRadius ** 2
        + random() * (1 - minimumStrandRadius ** 2),
      )
      : Math.sqrt(random()) * (0.88 + random() * 0.12)
    const radius = sampledRadius

    return {
      homeX: Math.cos(angle) * radius,
      homeY: Math.sin(angle) * radius * ELLIPSE_Y_SCALE,
      size: 0.55 + random() * 0.9,
      phase: random() * FULL_TURN,
      alpha: 0.42 + random() * 0.5,
    }
  })

  const outerNodeIndexes = nodes
    .map((node, index) => ({ index, radius: normalizedRadius(node) }))
    .filter(({ radius }) => radius > OUTER_RADIUS)
    .sort((first, second) => second.radius - first.radius || first.index - second.index)

  const strands: ProbeStrand[] = []
  const availableOrigins = outerNodeIndexes.map(({ index }) => index)
  while (strands.length < strandCount) {
    const selection = Math.floor(random() * availableOrigins.length)
    const [origin] = availableOrigins.splice(selection, 1)
    if (origin === undefined) {
      throw new Error('Unable to select a bounded probe-strand origin')
    }
    const node = nodes[origin]
    if (!node) {
      throw new Error('Probe-strand origin is outside the node collection')
    }
    const outwardAngle = Math.atan2(node.homeY / ELLIPSE_Y_SCALE, node.homeX)

    strands.push({
      origin,
      angle: outwardAngle + (random() - 0.5) * 0.34,
      length: 0.3 + random() * 0.45,
      bend: (random() - 0.5) * 0.48,
      phase: random() * FULL_TURN,
    })
  }

  return {
    nodes,
    edges: createEdges(nodes),
    strands,
  }
}
